import { useEffect, useRef, useState } from "react";
import api, { fileUrl, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { CalendarPlus, Upload, Trash2, Clock } from "lucide-react";

// Event Creator "My Account – Profile" page. Mirrors the Marketplace Seller
// layout. All edits are routed through the admin approval queue before
// going live (role_profile_edits).
const EMPTY = {
  business_name: "",
  organizer_name: "",
  tagline: "",
  description: "",
  about_event: "",
  event_name: "",
  company_logo: "",
  location: "",
  phone: "",
  contact_number: "",
  contact_email: "",
  email: "",
  website: "",
  social_links: "",
};

export default function EventCreatorProfile() {
  const { user } = useAuth();
  const [profile, setProfile] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [pendingEdit, setPendingEdit] = useState(null);
  const logoInput = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get("/role-profile");
      const p = data.profile || {};
      setProfile({
        ...EMPTY,
        business_name: p.business_name || p.organizer_name || "",
        organizer_name: p.organizer_name || p.business_name || "",
        tagline: p.tagline || "",
        description: p.description || "",
        about_event: p.about_event || "",
        event_name: p.event_name || "",
        company_logo: p.company_logo || "",
        location: p.location || "",
        phone: p.phone || p.contact_number || "",
        contact_number: p.contact_number || p.phone || "",
        contact_email: p.contact_email || p.email || "",
        email: p.email || p.contact_email || "",
        website: p.website || "",
        social_links: p.social_links || "",
      });
    } catch { /* ignore */ }
    try {
      const { data } = await api.get("/role-profile/pending-edit");
      setPendingEdit(data.pending_edit || null);
    } catch { /* ignore */ }
  };

  useEffect(() => { load(); }, []);

  const uploadLogo = async (file) => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) return toast.error("Image too large (max 10MB)");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/event-creator/upload?kind=logo", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setProfile((p) => ({ ...p, company_logo: data.path }));
      toast.success("Logo uploaded");
    } catch (e) {
      toast.error(errMsg(e, "Upload failed"));
    } finally {
      setUploading(false);
    }
  };

  const save = async () => {
    if (!profile.organizer_name.trim() && !profile.business_name.trim()) {
      return toast.error("Organizer / business name is required");
    }
    setSaving(true);
    try {
      const businessName = (profile.organizer_name || profile.business_name).trim();
      const payload = {
        business_name: businessName,
        organizer_name: businessName,
        tagline: profile.tagline,
        description: profile.description,
        about_event: profile.about_event,
        event_name: profile.event_name,
        company_logo: profile.company_logo,
        location: profile.location,
        phone: profile.contact_number || profile.phone,
        contact_number: profile.contact_number || profile.phone,
        contact_email: profile.email || profile.contact_email,
        email: profile.email || profile.contact_email,
        website: profile.website,
        social_links: profile.social_links,
      };
      const { data } = await api.put("/role-profile", payload);
      if (data?.pending_edit) {
        toast.success("Edit submitted for admin approval");
        setPendingEdit(data.pending_edit);
      } else {
        toast.success("Profile saved!");
      }
    } catch (e) {
      toast.error(errMsg(e, "Could not save profile"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto px-4 py-10" data-testid="ec-profile">
      <div className="flex items-center gap-3 mb-6">
        <span className="w-11 h-11 rounded-full bg-sage flex items-center justify-center"><CalendarPlus className="w-5 h-5 text-white" /></span>
        <div>
          <h1 className="text-2xl font-black text-forest">My account — Event Creator profile</h1>
          <p className="text-sm text-forest/60">Update your business details. Changes are reviewed before going live.</p>
        </div>
      </div>

      {pendingEdit && (
        <div className="bg-amber-100/70 border border-amber-300/50 rounded-2xl p-4 mb-6 flex items-start gap-3" data-testid="ec-profile-pending-banner">
          <Clock className="w-5 h-5 text-amber-700 shrink-0 mt-0.5" />
          <div>
            <p className="font-bold text-forest">Changes awaiting admin review</p>
            <p className="text-sm text-forest/70">Submitted {new Date(pendingEdit.created_at).toLocaleString()}. You'll be emailed once an admin approves them.</p>
          </div>
        </div>
      )}

      <div className="bg-white/80 border border-sage/20 rounded-3xl p-6 sm:p-8 shadow-sm space-y-4">
        <div>
          <Label>Company logo</Label>
          <div className="mt-2 flex items-center gap-4">
            {profile.company_logo ? (
              <div className="relative w-24 h-24 rounded-2xl overflow-hidden border border-sage/30 shrink-0">
                <img src={fileUrl(profile.company_logo)} alt="Company logo" className="w-full h-full object-cover" data-testid="ec-profile-logo-preview" />
                <button type="button" onClick={() => setProfile({ ...profile, company_logo: "" })}
                  className="absolute top-1 right-1 bg-white/90 hover:bg-white p-1 rounded-full text-terracotta-dark" aria-label="Remove logo" data-testid="ec-profile-logo-remove">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            ) : (
              <div className="w-24 h-24 rounded-2xl bg-softstone/60 border border-dashed border-sage/40 flex items-center justify-center text-forest/40 shrink-0">
                <Upload className="w-6 h-6" />
              </div>
            )}
            <div>
              <input ref={logoInput} type="file" accept="image/*" className="hidden"
                onChange={(e) => uploadLogo(e.target.files?.[0])}
                data-testid="ec-profile-logo-input" />
              <Button type="button" variant="outline" className="rounded-full border-sage/40 text-forest"
                onClick={() => logoInput.current?.click()} disabled={uploading} data-testid="ec-profile-logo-btn">
                <Upload className="w-4 h-4 mr-2" />
                {uploading ? "Uploading..." : (profile.company_logo ? "Replace logo" : "Upload logo")}
              </Button>
              <p className="text-xs text-forest/50 mt-1">Square JPG or PNG, up to 10MB.</p>
            </div>
          </div>
        </div>

        <div>
          <Label>Organizer / business name *</Label>
          <Input value={profile.organizer_name} onChange={(e) => setProfile({ ...profile, organizer_name: e.target.value })} className="mt-1" data-testid="ec-profile-organizer" />
        </div>
        <div>
          <Label>Tagline</Label>
          <Input value={profile.tagline} onChange={(e) => setProfile({ ...profile, tagline: e.target.value })} className="mt-1" placeholder="One-line pitch" data-testid="ec-profile-tagline" />
        </div>
        <div>
          <Label>About your business</Label>
          <Textarea value={profile.description} onChange={(e) => setProfile({ ...profile, description: e.target.value })} rows={4} className="mt-1" placeholder="Who you are and what families can expect..." data-testid="ec-profile-description" />
        </div>
        <div>
          <Label>Types of events you host</Label>
          <Input value={profile.event_name} onChange={(e) => setProfile({ ...profile, event_name: e.target.value })} className="mt-1" placeholder="e.g. Workshops, Nature walks" data-testid="ec-profile-event-name" />
        </div>
        <div>
          <Label>About the events you host</Label>
          <Textarea value={profile.about_event} onChange={(e) => setProfile({ ...profile, about_event: e.target.value })} rows={3} className="mt-1" placeholder="Format, age range, what makes them special..." data-testid="ec-profile-about-event" />
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label>Location / service area</Label>
            <Input value={profile.location} onChange={(e) => setProfile({ ...profile, location: e.target.value })} className="mt-1" data-testid="ec-profile-location" />
          </div>
          <div>
            <Label>Contact number</Label>
            <Input value={profile.contact_number} onChange={(e) => setProfile({ ...profile, contact_number: e.target.value, phone: e.target.value })} className="mt-1" data-testid="ec-profile-phone" />
          </div>
          <div>
            <Label>Contact email</Label>
            <Input value={profile.email} onChange={(e) => setProfile({ ...profile, email: e.target.value, contact_email: e.target.value })} className="mt-1" data-testid="ec-profile-email" />
          </div>
          <div>
            <Label>Website</Label>
            <Input value={profile.website} onChange={(e) => setProfile({ ...profile, website: e.target.value })} className="mt-1" placeholder="https://..." data-testid="ec-profile-website" />
          </div>
          <div className="sm:col-span-2">
            <Label>Social links</Label>
            <Input value={profile.social_links} onChange={(e) => setProfile({ ...profile, social_links: e.target.value })} className="mt-1" placeholder="Instagram, Facebook or TikTok handle / link" data-testid="ec-profile-social-links" />
          </div>
        </div>

        <Button onClick={save} disabled={saving} className="rounded-full bg-sage hover:bg-sage-dark text-white w-full" data-testid="ec-profile-save-btn">
          {saving ? "Saving..." : (user?.vendor_status === "approved" ? "Submit for approval" : "Save profile")}
        </Button>
      </div>
    </div>
  );
}
